import * as React from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme as useThemeHook } from '@mui/material/styles';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepConnector, {
	stepConnectorClasses,
} from '@mui/material/StepConnector';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { SxProps, Theme, styled } from '@mui/material';

const CustomConnector = styled(StepConnector)<{ activeStep: number }>(
	({ theme, activeStep }) => ({
		[`&.${stepConnectorClasses.alternativeLabel}`]: {
			top: 10,
			left: 'calc(-50% + 16px)',
			right: 'calc(50% + 16px)',
		},
		[`& .${stepConnectorClasses.line}`]: {
			borderColor: theme.palette.grey[400],
			borderTopWidth: 2,
			borderRadius: 1,
		},
		// Color the connector based on the step it's coming from
		[`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
			borderColor: theme.palette.primary.main,
		},
		[`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
			borderColor: theme.palette.primary.main,
		},
	})
);

interface DotsStepperProps {
	/** Total number of steps */
	totalSteps: number;
	/** Current active step (0-based index) */
	activeStep: number;
	/** Callback fired when the next button is clicked */
	onNext?: () => void;
	/** Callback fired when the back button is clicked */
	onBack?: () => void;
	/** Callback fired when a step is clicked */
	onStepClick?: (stepIndex: number) => void;
	/** Custom styles for the stepper component */
	sx?: SxProps<Theme>;
	/** Custom text for the next button */
	nextButtonText?: string;
	/** Custom text for the back button */
	backButtonText?: string;
	/** Whether to disable the next button */
	disableNext?: boolean;
	/** Whether to disable the back button */
	disableBack?: boolean;
	/** Whether to show navigation buttons */
	showButtons?: boolean;
	/** Custom step labels - if not provided, generic labels will be used */
	stepLabels?: string[];
	/** Whether to show only dots without labels */
	dotsOnly?: boolean;
}

export const DotsStepper: React.FC<DotsStepperProps> = ({
	totalSteps,
	activeStep,
	onNext,
	onBack,
	onStepClick,
	sx = { width: '100%' },
	nextButtonText = 'Next',
	backButtonText = 'Back',
	disableNext,
	disableBack,
	showButtons = true,
	stepLabels,
	dotsOnly = false,
}) => {
	const theme = useThemeHook();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Generate step labels if not provided
	const steps =
		stepLabels ||
		Array.from({ length: totalSteps }, (_, i) => `Step ${i + 1}`);

	// Mobile view: Show current step label and progress bar with back button
	if (isMobile) {
		const currentStepLabel = steps[activeStep] || `Step ${activeStep + 1}`;

		return (
			<Box sx={{ width: '100%', ...sx }}>
				{/* Header with label and back button */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 1.5,
					}}
				>
					<Typography
						variant="h6"
						sx={{
							color: theme.palette.primary.main,
							fontSize: '1.125rem',
							fontWeight: 500,
						}}
					>
						{currentStepLabel}
					</Typography>
					{activeStep > 0 && (
						<Button
							variant="text"
							onClick={onBack}
							sx={{
								color: theme.palette.primary.main,
								fontSize: '0.875rem',
								fontWeight: 600,
								textTransform: 'uppercase',
								minWidth: 'auto',
								padding: '4px 8px',
								'&:hover': {
									backgroundColor: 'transparent',
									opacity: 0.8,
								},
							}}
							startIcon={<KeyboardArrowLeft />}
						>
							Back
						</Button>
					)}
				</Box>

				{/* Progress bar */}
				<Box sx={{ position: 'relative', width: '100%' }}>
					{/* Background bars for all steps */}
					<Box
						sx={{
							display: 'flex',
							gap: '8px',
							width: '100%',
						}}
					>
						{Array.from({ length: totalSteps }).map((_, index) => (
							<Box
								key={index}
								sx={{
									flex: 1,
									height: '4px',
									backgroundColor:
										index <= activeStep
											? theme.palette.primary.main
											: theme.palette.grey[700],
									borderRadius: '2px',
									transition: 'background-color 0.3s ease',
								}}
							/>
						))}
					</Box>
				</Box>
			</Box>
		);
	}

	// Render simple dots-only version
	if (dotsOnly) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					gap: '12px',
					...sx,
				}}
			>
				{Array.from({ length: totalSteps }).map((_, index) => (
					<Box
						key={index}
						onClick={() => onStepClick?.(index)}
						sx={{
							width: '10px',
							height: '10px',
							borderRadius: '50%',
							backgroundColor:
								index === activeStep
									? theme.palette.primary.main
									: theme.palette.grey[400],
							cursor: onStepClick ? 'pointer' : 'default',
							transition: 'all 0.2s ease',
							'&:hover': onStepClick
								? {
										backgroundColor:
											index === activeStep
												? theme.palette.primary.main
												: theme.palette.grey[500],
								  }
								: {},
						}}
					/>
				))}
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%', ...sx }}>
			<Stepper
				activeStep={activeStep}
				alternativeLabel
				connector={<CustomConnector activeStep={activeStep} />}
				sx={{
					'& .MuiStepLabel-root .Mui-completed': {
						color: theme.palette.primary.main,
					},
					'& .MuiStepLabel-root .Mui-active': {
						color: theme.palette.primary.main,
					},
					'& .MuiStepLabel-root .Mui-active .MuiStepIcon-text': {
						fill: theme.palette.primary.contrastText,
					},
					...(onStepClick && {
						'& .MuiStepLabel-root': {
							cursor: 'pointer',
							'&:hover': {
								color: theme.palette.primary.main,
							},
						},
					}),
				}}
			>
				{steps.map((label, index) => (
					<Step key={label}>
						<StepLabel
							onClick={() => onStepClick?.(index)}
							sx={onStepClick ? { cursor: 'pointer' } : undefined}
						>
							{label}
						</StepLabel>
					</Step>
				))}
			</Stepper>

			{showButtons && (
				<Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
					<Button
						color="inherit"
						disabled={disableBack ?? activeStep === 0}
						onClick={onBack}
						sx={{ mr: 1 }}
					>
						{theme.direction === 'rtl' ? (
							<KeyboardArrowRight />
						) : (
							<KeyboardArrowLeft />
						)}
						{backButtonText}
					</Button>
					<Box sx={{ flex: '1 1 auto' }} />
					<Button
						onClick={onNext}
						disabled={disableNext ?? activeStep === totalSteps - 1}
					>
						{nextButtonText}
						{theme.direction === 'rtl' ? (
							<KeyboardArrowLeft />
						) : (
							<KeyboardArrowRight />
						)}
					</Button>
				</Box>
			)}
		</Box>
	);
};
